/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useContext, useState, useEffect, useMemo } from "react";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { Dialog, CardContent, Card, Autocomplete, TextField, Button, Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "hooks/useSnackbar";
import WebpageEditContext from "components/webpage-selector/context";
import GalleryContext from "components/webpage-selector/gallery/context";
import { useTranslation } from "react-i18next";
import { useAuth } from "cms/auth/hooks/use-auth";
import throttle from "lodash/throttle";

const MODEL_TYPE_NEWS_SUBTYPE = "model.news-subtype";

const NewsItemSelectDialog = (props) => {

    const { t } = useTranslation();
    const { token } = useAuth();
    const { showSnackbar } = useSnackbar();
    const searchFunction = SimpliCityAdminSDK.webpage.advancedSearch;
    const scrollableHeight = "480px";
    const filter = props.filter;
    const filterValue = props.filterValue;
    const onAddToFeaturedPages = props.onAddToFeaturedPages;

    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Support both context-based and prop-based dialog control
    const ctx = useContext(WebpageEditContext);
    const contextOpen = ctx?.open;
    const contextHandleClose = ctx?.handleClose;
    
    const dialogOpen = props.open !== undefined ? props.open : (contextOpen || false);
    const dialogHandleClose = props.onClose || contextHandleClose || (() => {});
    
    const {
        selectedWebpage,
        setSelectedWebpage,
        webpages,
        setWebpages,
        onSelectWebpage: contextOnSelectWebpage
    } = ctx || {};

    const onSelectWebpageHandler = (event, webpage) => {
        showSnackbar("onSelectWebpage");
        event.stopPropagation();
        setSelectedWebpage(webpage);
    };

    const fetch = useMemo(
        () =>
            throttle((request, callback) => {
                const input = request.input;
                if (!input || input.trim().length < 2) {
                    callback({ results: [] });
                    return;
                }
                setLoading(true);
                const params = {
                    term: input,
                    modelType: MODEL_TYPE_NEWS_SUBTYPE,
                    page: 1,
                    pageSize: 10
                };
                searchFunction(token, params).then((results) => {
                    setLoading(false);
                    callback(results);
                    return results;
                }).catch(() => {
                    setLoading(false);
                    callback({ results: [] });
                });
            }, 200),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [token],
    );

    useEffect(() => {
        if (inputValue === "" || inputValue === null) {
            setOptions(value ? [value] : []);
            return undefined;
        }

        const request = { input: inputValue };
        fetch(request, (searchResults) => {
            let newOptions = [];
            if (value) {
                newOptions = [value];
            }
            const results = searchResults?.results || [];
            const search_set = results.map((result) => ({
                id: result.id,
                guid: result.guid,
                label: result.nav_title || result.title || result.file_name,
                text: result.nav_title || result.title || result.file_name,
                webpage: result,
            }));
            if (results) {
                newOptions = [...newOptions, ...search_set];
            }
            setOptions(newOptions);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, inputValue, fetch]);

    const handleSelect = () => {
        if (value && value.webpage) {
            // If opened from context (existing item), update that item
            // Otherwise, if opened from Add button, add new item
            if (contextOnSelectWebpage && ctx) {
                // Update existing item via context
                contextOnSelectWebpage(value.webpage);
            } else if (onAddToFeaturedPages) {
                // Add new item (from Add button)
                onAddToFeaturedPages(value.webpage);
            }
            setValue(null);
            setInputValue("");
            if (dialogHandleClose) {
                dialogHandleClose();
            }
        }
    };

    const galleryCTX = {
        webpages,
        setWebpages,
        searchFunction,
        scrollableHeight,
        onSelectWebpage: onSelectWebpageHandler,
        selectedWebpage,
        setSelectedWebpage,
        showSnackbar,
        filter,
        filterValue
    };

    return (
        <Fragment>
            <Dialog
                open={dialogOpen}
                onClose={dialogHandleClose}
                fullWidth={true}
                maxWidth='lg'
                className='simplicity-image-selector-overlay'
            >
                <GalleryContext.Provider value={galleryCTX}>
                    <Card elevation={0} className='simplicity-webpage-selector-window'>
                        <CardContent sx={{ flex: "1 0 auto", height: "50%" }}>
                            <Box sx={{ padding: "16px" }}>
                                <Autocomplete
                                    value={value}
                                    onChange={(event, newValue) => {
                                        setValue(newValue);
                                    }}
                                    inputValue={inputValue}
                                    onInputChange={(event, newInputValue) => {
                                        setInputValue(newInputValue);
                                    }}
                                    options={options}
                                    getOptionLabel={(option) => {
                                        if (typeof option === "string") {
                                            return option;
                                        }
                                        return option?.label || option?.text || "";
                                    }}
                                    isOptionEqualToValue={(option, value) => {
                                        return option?.guid === value?.guid;
                                    }}
                                    loading={loading}
                                    noOptionsText="Type to search webpages..."
                                    sx={{ marginBottom: "16px" }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search webpages..."
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                                    <Button variant='outlined' color='secondary' onClick={dialogHandleClose}>
                                        {t("commons.cancel")}
                                    </Button>
                                    <Button 
                                        variant='contained' 
                                        color='primary' 
                                        onClick={handleSelect}
                                        disabled={!value}
                                    >
                                        {t("commons.select")}
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </GalleryContext.Provider>
            </Dialog>
        </Fragment>
    );
};

export { NewsItemSelectDialog };

